import { DecoratorNode, createCommand } from 'lexical';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback } from 'react';

export const INSERT_PAGE_BREAK_COMMAND = createCommand('INSERT_PAGE_BREAK_COMMAND');

function PageBreakComponent({ nodeKey }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

  const onClick = useCallback(() => {
    clearSelection();
    setSelected(true);
  }, [clearSelection, setSelected]);

  return (
    <div
      className={`lexical-page-break-visual${isSelected ? ' selected' : ''}`}
      onClick={onClick}
      contentEditable={false}
    >
      <span className="lexical-page-break-label">Page Break</span>
    </div>
  );
}

export class PageBreakNode extends DecoratorNode {
  static getType() { return 'page-break'; }

  static clone(node) { return new PageBreakNode(node.__key); }

  static importJSON() { return $createPageBreakNode(); }

  // Pairs with exportDOM() below so Lexical can round-trip a page break through
  // HTML — most importantly, pasting HTML that contains a `.doc-page-break` div
  // restores it as a PageBreakNode rather than dropping to plain text. Without
  // this, Lexical logs a dev warning because exportDOM exists but importDOM doesn't.
  static importDOM() {
    return {
      div: (node) => {
        if (node?.classList?.contains('doc-page-break')) {
          return {
            conversion: () => ({ node: $createPageBreakNode() }),
            priority: 2,
          };
        }
        return null;
      },
    };
  }

  exportJSON() { return { type: 'page-break', version: 1 }; }

  createDOM() {
    const el = document.createElement('div');
    el.className = 'lexical-page-break-dom';
    return el;
  }

  updateDOM() { return false; }

  exportDOM() {
    const el = document.createElement('div');
    // No inline page-break styles here — the PDF export pipeline splits on
    // this div's class and reconstructs pages with page-break-before:always
    // on each segment. Adding the style here causes html2pdf to insert an
    // extra blank page on top of the split-based break.
    el.className = 'doc-page-break';
    return { element: el };
  }

  isInline() { return false; }

  decorate() {
    return <PageBreakComponent nodeKey={this.__key} />;
  }
}

export function $createPageBreakNode() {
  return new PageBreakNode();
}

export function $isPageBreakNode(node) {
  return node instanceof PageBreakNode;
}
